import { defineStore, acceptHMRUpdate } from "pinia";
import { brogglBack } from "../axios.js";
import { teamReportTypes } from "../assets/variables.js";
import dayjs from "dayjs";
import { get } from "lodash";

export const useTeamReportStore = defineStore("teamReport", {
  state: () => ({
    teams: [],
    total: 0,
    chart: {},
    barChartKey: 0,
    empty: false,
    chartData: {
      labels: [],
      datasets: [],
    },
    parameters: {
      team: "",
      period: "Last Quarter",
      breakdown: "Month",
      teamReportType: teamReportTypes[0].value,
    },
    totals: {},
    campaignsForTeam: [],
    isBillableDataLoaded: false,
  }),
  getters: {
    getTeams: (state) => state.teams,
    getTotal: (state) => state.total,
    getChart: (state) => state.chart,
    getParameters: (state) => state.parameters,
    getBarChartKey: (state) => state.barChartKey,
    getChartData: (state) => state.chartData,
    getEmpty: (state) => state.empty,
    getTotals: (state) => state.totals,
    getCampaignsForTeam: (state) => state.campaignsForTeam,
    getIsBillableDataLoaded: (state) => state.isBillableDataLoaded,
  },
  actions: {
    async fetchTeams() {
      const res = await brogglBack.get("/team/teams", {
        headers: {
          Authorization: "Bearer " + window.$cookies.get("jwt"),
        },
      });
      this.teams = res.data.data;
      this.parameters.team = this.teams[0].ID;
    },
    async getReport() {
      this.chart = {};
      this.chartData = {
        labels: [],
        datasets: [],
      };
      this.total = 0;
      this.empty = false;
      if (this.parameters.teamReportType === "project") {
        const res = await brogglBack.get("/report/team/project-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            breakdown: this.parameters.breakdown,
            period: this.parameters.period,
            teamId: this.parameters.team,
          },
        });
        let sumCS = [];
        let sumPS = [];
        let sumOnBoarding = [];
        let sumOtherProjects = [];
        console.log(res);
        for (let i = 0; i < res.data.data.length; i++) {
          this.total += res.data.data[i].sumAllProjects;
          sumCS.push(res.data.data[i].sumCS);
          sumPS.push(res.data.data[i].sumPS);
          sumOnBoarding.push(res.data.data[i].sumOnBoarding);
          sumOtherProjects.push(res.data.data[i].sumOtherProjects);
        }
        this.chartData.datasets.push({
          label: "CS",
          backgroundColor: "#217981",
          borderColor: "#000000",
          data: sumCS,
        });
        this.chartData.datasets.push({
          label: "PS",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          data: sumPS,
        });
        this.chartData.datasets.push({
          label: "OnBoarding",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          data: sumOnBoarding,
        });
        this.chartData.datasets.push({
          label: "Other Projects",
          backgroundColor: "#48d4d4",
          borderColor: "#000000",
          data: sumOtherProjects,
        });

        this.chartData.labels = res.data.data.map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data;
      } else {
        this.isBillableDataLoaded = false;
        const res = await brogglBack.get("/report/team/billable-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            breakdown: this.parameters.breakdown,
            period: this.parameters.period,
            teamId: this.parameters.team,
          },
        });
        let sumBillable = [];
        let sumNonBillable = [];
        let sumInternal = [];
        for (let i = 0; i < res.data.data.report.length; i++) {
          this.total += res.data.data.report[i].sumAll;
          sumBillable.push(res.data.data.report[i].sumBillable);
          sumNonBillable.push(res.data.data.report[i].sumNonBillable);
          sumInternal.push(res.data.data.report[i].sumInternal);
        }
        this.chartData.datasets.push({
          label: "Billable",
          backgroundColor: "#217981",
          borderColor: "#000000",
          fill: false,
          data: sumBillable,
        });
        this.chartData.datasets.push({
          label: "Non Billable",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          fill: false,
          data: sumNonBillable,
        });
        this.chartData.datasets.push({
          label: "Internal",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          fill: false,
          data: sumInternal,
        });
        this.campaignsForTeam = res.data.data.campaignForTeam;
        this.chartData.labels = res.data.data.report.map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data.report;
        this.isBillableDataLoaded = true;
      }
      this.barChartKey++;
      this.empty = this.chartData.datasets.every(
        (dataset) => dataset.data.length === 0,
      );
      this.total = this.total.toLocaleString();
    },
    calculateTotals() {
      this.totals = {};
      if (this.parameters.teamReportType === "project") {
        this.totals.CS = this.chart.reduce((acc, curr) => acc + curr.sumCS, 0);
        this.totals.PS = this.chart.reduce((acc, curr) => acc + curr.sumPS, 0);
        this.totals.OnBoarding = this.chart.reduce(
          (acc, curr) => acc + curr.sumOnBoarding,
          0,
        );
        this.totals.OtherProjects = this.chart.reduce(
          (acc, curr) => acc + curr.sumOtherProjects,
          0,
        );
        this.total = ['CS', 'PS', 'OnBoarding', 'OtherProjects']
          .map((key) => this.totals[key] || 0)
          .reduce((sum, val) => sum + val, 0);
      } else {
        this.totals.Billable = this.chart.reduce(
          (acc, curr) => acc + curr.sumBillable,
          0,
        );
        this.totals.NonBillable = this.chart.reduce(
          (acc, curr) => acc + curr.sumNonBillable,
          0,
        );
        this.totals.Internal = this.chart.reduce(
          (acc, curr) => acc + curr.sumInternal,
          0,
        );
        this.total = ['Billable', 'NonBillable', 'Internal']
          .map((key) => this.totals[key] || 0)
          .reduce((sum, val) => sum + val, 0);
      }
    },
    convertArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args.data;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      let keys = Object.keys(data[0]);
      result += keys.join(columnDelimiter) + lineDelimiter;
      data.forEach(function (item) {
        Object.keys(item).forEach((key) => {
          result += item[key] + columnDelimiter;
        });
        result += lineDelimiter;
      });
      return result;
    },
    csvExport(arrData) {
      var data, filename, link;
      var csv = this.convertArrayOfObjectsToCSV({
        data: arrData,
      });
      if (csv == null) return;
      filename = arrData.filename || "export.csv";
      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      data = encodeURI(csv);
      link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTeamReportStore, import.meta.hot));
}
